import './App.css';
import LogInForm from './components/loginform';
import Header from './components/header';
import CylindersFilling from './components/cylinderAdder';
import AdminDashboard from './components/adminDashboard';
import AdminReports from './components/AdminReports';
import ErrorBoundary from './components/ErrorBoundary';
import SalesDashboard from './components/salesDashboard';
import ProductionTable from './components/productionTable';
import PrivateRoute from './components/PrivateRoute';
import { FillingProvider } from './context/FillingContext';
import 'bootstrap-icons/font/bootstrap-icons.css';
import WallViewerDashboard from './components/WallViewerDashboard';
import { Chart as ChartJS } from 'chart.js';
import isTokenExpired from './utils/isTokenExpired';
import { checkForUpdate } from './utils/checkForUpdate';
import { registerAndSubscribe } from './utils/pushNotifications';



import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';

ChartJS.defaults.color = '#9DB3C2';
ChartJS.defaults.borderColor = 'rgba(234,242,245,0.10)';
ChartJS.defaults.font.family = "'IBM Plex Sans', sans-serif";

function App() {
  useEffect(() => {
    checkForUpdate();
  }, []);

  const [fillings, setFillings] = useState([]);

  const refreshTable = async () => {
  try {
    const res = await fetch('/api/fillings/today');
    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
    const data = await res.json();
    setEntries(data); // Update state with today's data
  } catch (err) {
    console.error('❌ Error fetching today\'s fillings:', err);
  }
};


  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    const hasSession = !!localStorage.getItem('userName');
    if (hasSession && isTokenExpired(localStorage.getItem('token'))) {
      // Stale session from a previous visit (token expired while the app was
      // closed) — clear it now rather than rendering a protected dashboard
      // that's guaranteed to 401 on its first fetch. Without this, the app
      // briefly mounts the authenticated view with a dead token, every
      // component's data fetch fails at once, and only then does the 401
      // interceptor redirect to /login — a race that can crash the page to
      // blank instead of showing the login screen cleanly.
      localStorage.removeItem('userType');
      localStorage.removeItem('userName');
      localStorage.removeItem('token');
      return false;
    }
    return hasSession;
  });

  // A restored session (valid token already in localStorage) skips
  // loginform.js entirely, so that's not enough to keep push subscriptions
  // fresh — if iOS/the browser ever rotates or drops the underlying
  // registration in the background, nothing re-subscribes until the 12h
  // token expires and the user re-enters credentials. Re-verify on every
  // authenticated app load instead so a stale subscription self-heals.
  useEffect(() => {
    if (isLoggedIn) registerAndSubscribe();
  }, [isLoggedIn]);

  const [userType, setUserType] = useState(() => localStorage.getItem('userType') || '');
  const [userName, setUserName] = useState(() => localStorage.getItem('userName') || '');

  const [users, setUsers] = useState(() => {
    const storedUsers = localStorage.getItem('users');
    return storedUsers ? JSON.parse(storedUsers) : [];
  });

  const [activityLog, setActivityLog] = useState(() => {
    const storedLog = localStorage.getItem('activityLog');
    return storedLog ? JSON.parse(storedLog) : [];
  });

  const [entries, setEntries] = useState(() => {
    const storedEntries = localStorage.getItem('entries');
    return storedEntries ? JSON.parse(storedEntries) : [];
  });

  const [clientsList, setClientsList] = useState(() => {
    const storedClients = localStorage.getItem('clientsList');
    return storedClients ? JSON.parse(storedClients) : ['Client A', 'Client B', 'Client C'];
  });

  // Three independently-tracked streams: Operación (the plant physically
  // running — gates the dryer regen panel), and O2/N2 gas production
  // (mutually exclusive — gates the filling forms). See
  // GET /api/production-events/current for the shape.
  const [productionStatus, setProductionStatus] = useState({
    operacion: { isRunning: false },
    o2: { isRunning: false },
    n2: { isRunning: false },
    activeGas: null,
  });

  useEffect(() => {
  const fetchProductionState = async () => {
    try {
      const res = await fetch('/api/production-events/current');
      const data = await res.json();
      // This effect runs unconditionally from mount, including on the
      // login page before a token exists — an unauthenticated/error
      // response has no `operacion` key, and replacing state with it
      // wholesale would break every consumer of productionStatus.operacion.
      if (data?.operacion) setProductionStatus(data);
    } catch (err) {
      console.error('❌ Error fetching production state:', err);
    }
  };

  fetchProductionState();
  const intervalId = setInterval(fetchProductionState, 30000);
  return () => clearInterval(intervalId);
}, []);



  const [cylinderContributions, setCylinderContributions] = useState(() => {
    const storedContributions = localStorage.getItem('cylinderContributions');
    return storedContributions ? JSON.parse(storedContributions) : [];
  });

  useEffect(() => {
    refreshTable(); // Call refresh on mount
  }, []);

  useEffect(() => {
    if (!Array.isArray(cylinderContributions)) {
      setCylinderContributions([]); // Ensure it's an array
    }
  }, [cylinderContributions]);

  const addCylinderContribution = (clientName, cylindersAdded) => {
    setCylinderContributions((prev) => [
      ...prev,
      {
        clientName,
        cylindersAdded,
        timestamp: new Date().toISOString(),
      },
    ]);
  };

  // Sync data to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('users', JSON.stringify(users));
  }, [users]);

  useEffect(() => {
    localStorage.setItem('activityLog', JSON.stringify(activityLog));
  }, [activityLog]);

  useEffect(() => {
    localStorage.setItem('entries', JSON.stringify(entries));
  }, [entries]);

  useEffect(() => {
    localStorage.setItem('clientsList', JSON.stringify(clientsList));
  }, [clientsList]);

  useEffect(() => {
    localStorage.setItem('productionStatus', JSON.stringify(productionStatus));
  }, [productionStatus]);

  useEffect(() => {
    localStorage.setItem('cylinderContributions', JSON.stringify(cylinderContributions));
  }, [cylinderContributions]);

  const addLogEntry = (message) => {
    const timestamp = new Date().toISOString(); // shown with formatDateTime; kept parseable for the activity charts
    setActivityLog((prevLog) => [{ timestamp, message }, ...prevLog]);
  };

 const addClient = async (client) => {
  try {
    const res = await fetch('/api/clients', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: client, userName }),
    });

    const data = await res.json();

    if (!res.ok) {
      alert(data.error || 'Error al guardar el cliente.');
      return;
    }
  } catch (err) {
    console.error('❌ Error saving client:', err);
    alert('Error de conexión al guardar el cliente.');
  }
};

  const editClientName = async (oldName, newName) => {
    const res = await fetch('/api/clients/rename', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ oldName, newName, userName }),
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.error || 'Error al editar el cliente.');
    }

    return data;
  };

  const mergeClients = async (sourceNames, targetName) => {
    const res = await fetch('/api/clients/merge', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sourceNames, targetName }),
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.error || 'Error al fusionar los clientes.');
    }

    return data;
  };

  const deleteClient = async (name, adminUsername, adminPassword) => {
    const res = await fetch('/api/clients', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, adminUsername, adminPassword }),
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.error || 'Error al eliminar el cliente.');
    }

    return data;
  };


  const handleAddEntry = (newEntry) => {
    setEntries((prev) => [...prev, newEntry]);
    addLogEntry(`Filling entry added by ${userName}: ${newEntry.fillingID}`);
  };

  const handleLogin = (user) => {
    localStorage.setItem('userType', user.role);
    localStorage.setItem('userName', user.username);
    setIsLoggedIn(true);
    setUserType(user.role);
    setUserName(user.username);

    return user.role === 'admin' ? '/admin' : user.role === 'sales' ? '/sales' : user.role ==='viewer' ? '/dashboard' : '/home';
  };

  const handleLogOut = () => {
    fetch('/api/login/logout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: userName }),
    }).catch((err) => console.error('❌ Error logging logout:', err));

    localStorage.removeItem('userType');
    localStorage.removeItem('userName');
    localStorage.removeItem('token');
    setIsLoggedIn(false);
    setUserType('');
    setUserName('');
  };

  return (
    <FillingProvider>
    <Router>
      <div className="App">
        <Header isLoggedIn={isLoggedIn} onLogOut={handleLogOut} userName={userName} userType={userType} addLogEntry={addLogEntry} />
        <Routes>
          <Route path="/login" element={<LogInForm handleLogin={handleLogin} />} />

          <Route
            path="/home"
            element={
              <PrivateRoute isLoggedIn={isLoggedIn} userType={userType} allowedRoles={['filler']}>
                <ErrorBoundary>
                  <div>
                    <h2 className="app-title" style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '2rem', color: 'var(--mm-cyan)', fontWeight: 700 }}>
                      MANIFOLD MANAGER
                    </h2>
                    <CylindersFilling
                      addEntry={handleAddEntry}
                      clientsList={clientsList}
                      addClient={addClient}
                      addLogEntry={addLogEntry}
                      productionStatus={productionStatus}
                      setProductionStatus={setProductionStatus}
                      userName={userName}
                      cylinderEntries={entries}
                      setEntries={setEntries}
                      refreshTable={refreshTable}
                    />

                  </div>
                </ErrorBoundary>
              </PrivateRoute>
            }
          />

          <Route
            path="/admin"
            element={
              <PrivateRoute isLoggedIn={isLoggedIn} userType={userType} allowedRoles={['admin']}>
                <ErrorBoundary>
                  <AdminDashboard
                    users={users}
                    setUsers={setUsers}
                    entries={entries}
                    activityLog={activityLog}
                    addLogEntry={addLogEntry}
                    userName={userName}
                    cylinderContributions={cylinderContributions}
                    setCylinderContributions={setCylinderContributions}
                  />
                </ErrorBoundary>
              </PrivateRoute>
            }
          />

          <Route
            path="/admin/reportes"
            element={
              <PrivateRoute isLoggedIn={isLoggedIn} userType={userType} allowedRoles={['admin']}>
                <ErrorBoundary>
                  <AdminReports />
                </ErrorBoundary>
              </PrivateRoute>
            }
          />

         <Route
  path="/dashboard"
  element={
    <PrivateRoute isLoggedIn={isLoggedIn} userType={userType} allowedRoles={['viewer']}>
      <ErrorBoundary>
        <WallViewerDashboard />
      </ErrorBoundary>
    </PrivateRoute>
  }
/>

          <Route
            path="/sales"
            element={
              <PrivateRoute isLoggedIn={isLoggedIn} userType={userType} allowedRoles={['sales', 'admin']}>
                <ErrorBoundary>
                  <SalesDashboard
                    cylinderContributions={cylinderContributions}
                    setCylinderContributions={setCylinderContributions}
                    userName={userName}
                    addCylinderContribution={addCylinderContribution}
                    clientsList={clientsList}
                    addClient={addClient}
                    editClient={editClientName}
                    deleteClient={deleteClient}
                    mergeClients={mergeClients}
                  />
                </ErrorBoundary>
              </PrivateRoute>
            }
          />
          <Route path="*" element={<Navigate to={isLoggedIn ? '/home' : '/login'} replace />} />
        </Routes>

      </div>
    </Router>
    </FillingProvider>
  );
}

export default App;
